import { useRef, useState } from 'react';
import { useForm } from 'react-jeff';
import ReCAPTCHA from 'react-google-recaptcha';

import {
  AddressPrequalification,
  categoryIds,
  cableCouponCodeOnA12MonthContract,
  dataCouponCodeOnA12MonthContract,
  dataCouponCodeNoContract,
  CouponTypes,
  createCustomerDetails,
  createOrderDetails,
  createRequestBody,
  CustomerDetails,
  messages,
  OrderDetails,
  orderStoreApi,
  SPLITIO_KEY,
  T_Coupon,
  useAccountDetailsStore,
  useAnalytics,
  useBroadband,
  useBroadbandDeliveryStore,
  useCouponStore,
  useKeyThingsToKnowField,
  useOrderStore,
  useSplitIO,
  useTermsAndConditions,
  useToastContainer,
  useVoiceStore,
  ORDER_ERROR_MSGS,
  PACKAGE_TYPE,
  useProductInCart,
} from '@sky-tv-group/shared';
import { addressService, orderService, prequalService, sentryService, trackerService, kkService } from '../services';
import { useStore } from '../store';
import { IS_CAPTCHA_ENABLED } from '../config';
import history from '../history';
import { VoucherDetails } from '@sky-tv-group/shared/src/types';

/**
 * Hook for BB SPA review page, includes logic for submitting orders
 *
 */
function useReview() {
  const [submitting, setSubmitting] = useState(false);
  const [useTechnicianAvailableTimes] = useSplitIO(SPLITIO_KEY.SKYWEB_TECHNICIAN_LOOKUP);
  const [useNewOrderEndpoint] = useSplitIO(SPLITIO_KEY.SKYWEB_USE_ORDER_PROCESSOR_ENDPOINT);
  const [accountDetails] = useAccountDetailsStore(state => [state.accountDetails]);
  const { prequalData } = useBroadbandDeliveryStore();
  const { addToast } = useToastContainer();
  const { coupons } = useCouponStore();
  const { selectedTuiAddress, isBundle, setAccountNumber, setEmail, setFirstName } = useStore();
  const { details, deliveryDetails, getPrequalData } = useBroadband(prequalService);
  const { order } = useOrderStore();
  const { orderCompleted } = useAnalytics();
  const voiceDetails = useVoiceStore(s => s.details);
  const { starterInCart } = useProductInCart();

  const handleNothingToSubmit = () => {
    addToast({
      title: 'Warning',
      message: 'You do not have anything to upgrade currently. Please add a new package first.',
      type: 'warning',
    });
  };

  const handleErrors = (error: any) => {
    const message = messages.problemWithOrder;
    if (error?.response?.status === 400) {
      addToast({
        title: 'Alert',
        message: ORDER_ERROR_MSGS.RETURNING_CUSTOMER,
        type: 'error',
      });
    } else {
      addToast({
        title: 'Alert',
        message: `${message}`,
        type: 'error',
      });
    }
  };

  const getOrderContext = (details: OrderDetails | undefined, customer: CustomerDetails | undefined) => {
    return {
      // additional contexts for orders
      contexts: {
        orderDetails: {
          products: JSON.stringify(details?.products),
        },
      },
      user: {
        email: customer?.email,
        name: `${customer?.firstName} ${customer?.lastName}`,
      },
    };
  };

  const captchaRef = useRef<ReCAPTCHA>();
  const generateCaptchaToken = async (): Promise<string> => {
    const captcha = captchaRef.current;
    if (captcha) {
      const token = await captcha.executeAsync();
      if (token) {
        // need to reset captcha otherwise secondary click doesn't seem to work properly and takes long time with null token
        captcha.reset();
        return token;
      }
    }
    return '';
  };
  /**
   * Add DTH 12mth contract if there's no other DTH contract (Applies only to BB + DTH bundle acquisition flow)
   */

  async function addDefaultCableCoupon(coupons: T_Coupon[]): Promise<T_Coupon[]> {
    if (coupons.length === 0) {
      let defaultCoupon = await kkService.getCoupon(cableCouponCodeOnA12MonthContract);
      return [defaultCoupon!];
    }
    return coupons;
  }

  /**
   * Add BB contract if there is no other BB contract as follows:
   * - '12mth Contract' IF no other DTH coupon is present (AKA BB only acquisition flow)
   * - 'No Contract' IF there is a DTH coupon present (AKA BB + DTH Bundle acquisition flow)
   */
  async function addDefaultDataCoupon(coupons: T_Coupon[], cableCoupons?: T_Coupon[]): Promise<T_Coupon[]> {
    if (coupons.length === 0) {
      let defaultCoupon = await kkService.getCoupon(
        cableCoupons && cableCoupons.length > 0 ? dataCouponCodeNoContract : dataCouponCodeOnA12MonthContract
      );
      return [defaultCoupon!];
    }
    return coupons;
  }

  const termsAndConditionsField = useTermsAndConditions();
  const keyThingsToKnowField = useKeyThingsToKnowField();
  const prequal = new AddressPrequalification(prequalData?.data);
  const { voucher } = useCouponStore();
  const [isVoucherEnabled] = useSplitIO(SPLITIO_KEY.SKYWEB_VOUCHER_ENABLE);

  const form = useForm({
    fields: [termsAndConditionsField, keyThingsToKnowField],
    onSubmit: async () => {
      // we can use order products here since this is acquisition (new customer)
      // and that all products in orderStore is not yet subscribed
      const orderProducts = order?.orderProducts || [];
      const { yourDetail, installationDetail, propertyDetail, marketingEmails } = accountDetails;
      const customerDetails = await createCustomerDetails(
        yourDetail,
        installationDetail,
        propertyDetail,
        addressService,
        undefined,
        marketingEmails,
        undefined,
        undefined,
        false
      );
      let orderDetails: OrderDetails | undefined;
      if (orderProducts.length === 0) {
        handleNothingToSubmit();
      } else {
        setSubmitting(true);
        try {
          /**
           * Silent Campaign logic:
           * UPGRADE to BB: NO BB contract. (Scenario handled within in MyAccount)
           * ACQUISITION BUNDLE: DTH 12 mth contract + BB No Contract
           * ACQUISITION BB ONLY: BB 12mth contract
           */
          let cableCoupons = coupons.filter(c => c.custom5 === CouponTypes.Cable);
          let dataVoiceCoupons = coupons.filter(
            c => c.custom5 === CouponTypes.Broadband || c.custom5 === CouponTypes.Voice
          );
          if (isBundle) {
            cableCoupons = await addDefaultCableCoupon(cableCoupons);
            dataVoiceCoupons = await addDefaultDataCoupon(dataVoiceCoupons, cableCoupons);
          } else {
            dataVoiceCoupons = await addDefaultDataCoupon(dataVoiceCoupons);
          }

          const hasCableProducts = orderProducts.some(p => categoryIds.package === p.categoryId);
          const cableOrderDetails = hasCableProducts
            ? createOrderDetails(orderProducts, cableCoupons, installationDetail, !!useTechnicianAvailableTimes)
            : null;

          const customer = {
            tuiAddressCode: selectedTuiAddress?.toString() ?? '',
            firstName: customerDetails.firstName,
            lastName: customerDetails.lastName,
            serviceContactEmail: customerDetails.email,
          };

          const dataOrderDetails = await createRequestBody(
            trackerService,
            addressService,
            undefined,
            details,
            deliveryDetails,
            orderProducts.map(op => op.product),
            [],
            customer,
            undefined,
            dataVoiceCoupons,
            getPrequalData,
            undefined,
            voiceDetails,
            undefined,
            undefined,
            false
          );

          // Combine DTH and Data order products
          orderDetails = {
            products: [...(cableOrderDetails ? cableOrderDetails.products : []), ...dataOrderDetails.products],
            voucherCode: voucher?.voucherCode,
          };

          if (voucher && isVoucherEnabled) {
            var voucherDetails: VoucherDetails = {
              skus: voucher?.skus!,
              coupons: voucher?.coupon!,
              description: voucher?.description!,
              voucherCode: voucher?.voucherCode!,
              url: voucher?.url ?? '',
            };
          }
          const order = orderStoreApi.getState().order!;
          const token = IS_CAPTCHA_ENABLED ? await generateCaptchaToken() : '';
          const response = await orderService.postNewOrder(
            order,
            customerDetails,
            orderDetails,
            token,
            !!useNewOrderEndpoint,
            null,
            voucherDetails!
          );

          // analytics -- there is a possibility that the work order number is empty
          const workOrderNumber = response?.initialOrderId;
          setAccountNumber(response?.accountNumber);
          setEmail(customerDetails.email);
          setFirstName(customerDetails.firstName);
          orderCompleted(
            workOrderNumber,
            false,
            order,
            coupons,
            isBundle ? PACKAGE_TYPE.DTH_BROADBAND : starterInCart ? PACKAGE_TYPE.DTH : PACKAGE_TYPE.BROADBAND
          );

          if (response.errors?.length > 0) {
            const orderError = new Error(
              `Broadband order error ${response.errors[0].description}: ${response.errors[0].message}`
            );
            sentryService.captureException(orderError, getOrderContext(orderDetails, customerDetails));
          }
          history.push({ pathname: '/confirmation', state: { workOrderNumber } });
        } catch (err) {
          handleErrors(err);
          sentryService.captureException(
            new Error('Broadband order error'),
            getOrderContext(orderDetails, customerDetails)
          );
        } finally {
          setSubmitting(false);
        }
      }
    },
  });

  return {
    form,
    prequal,
    submitting,
    captchaRef,
    accountDetails,
    broadbandDetails: details,
    broadbandDeliveryDetails: deliveryDetails,
    termsAndConditionsField,
    keyThingsToKnowField,
  };
}

export { useReview };
